import { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { useUrlParser } from '../../../../utils/useUrlParser';
import { getPatientAppointmentsSkeleton, getPatientAppointmentsDetails } from '../../../../api';

// Normalize details item: lift division/institution from doctor.jobs to top level
// (details endpoint nests them inside doctor.jobs, but Doctor component expects top-level props)
const normalizeDetail = (item) => {
  // If details API already returns division/institution at top level — use as-is
  if (item.division && item.institution) return item;

  const divisionId = item.job?.division_id;

  // Exact match by division_id (handle both string and number types)
  const matchingJob = divisionId != null
    ? item.doctor?.jobs?.find((j) => Number(j.division?.id) === Number(divisionId))
    : null;

  // Fallback to first job if no exact match
  const resolvedJob = matchingJob || item.doctor?.jobs?.[0];

  if (process.env.NODE_ENV !== 'production' && !resolvedJob) {
    console.warn('[AppointmentsV2] normalizeDetail: no job found for appointment', item.id, item);
  }

  return {
    ...item,
    division: resolvedJob?.division || null,
    institution: resolvedJob?.institution || null,
  };
};

// Reusable hook for one skeleton section (current or past)
function useSectionSkeleton({ patientId }) {
  const [skeleton, setSkeleton] = useState(null);
  const [detailsMap, setDetailsMap] = useState({});
  const [loadingProviders, setLoadingProviders] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const fetchingProviders = useRef(new Set());

  const load = useCallback(
    (past, page) => {
      if (patientId == null) return;
      setSkeleton(null);
      setDetailsMap({});
      setLoadingProviders(new Set());
      fetchingProviders.current = new Set();
      setLoading(true);

      getPatientAppointmentsSkeleton(patientId, past, page)
        .then((res) => {
          setSkeleton(res.data);
          if (res.pagination) setPagination(res.pagination);
          setLoading(false);
        })
        .catch(() => {
          setSkeleton([]);
          setLoading(false);
        });
    },
    [patientId],
  );

  const reset = useCallback(() => {
    setSkeleton(null);
    setDetailsMap({});
    setLoadingProviders(new Set());
    fetchingProviders.current = new Set();
    setPagination(null);
  }, []);

  const markDeleted = useCallback((id) => {
    setDetailsMap((prev) => {
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], deleted: true } };
    });
  }, []);

  // Fetch details per provider when skeleton arrives
  useEffect(() => {
    if (!skeleton?.length || patientId == null) return;

    const groups = skeleton.reduce((acc, item) => {
      if (!acc[item.provider_id]) acc[item.provider_id] = [];
      acc[item.provider_id].push(item.id);
      return acc;
    }, {});

    const newProviders = Object.entries(groups).filter(
      ([pid]) => !fetchingProviders.current.has(Number(pid)),
    );

    if (!newProviders.length) return;

    const newProviderIds = newProviders.map(([id]) => Number(id));
    newProviderIds.forEach((id) => fetchingProviders.current.add(id));

    setLoadingProviders((prev) => {
      const next = new Set(prev);
      newProviderIds.forEach((id) => next.add(id));
      return next;
    });

    newProviders.forEach(([providerId, ids]) => {
      const numericId = Number(providerId);

      getPatientAppointmentsDetails(patientId, numericId, ids)
        .then((res) => {
          setDetailsMap((prev) => {
            const next = { ...prev };
            res.data.forEach((item) => {
              next[item.id] = normalizeDetail(item);
            });
            return next;
          });
        })
        .catch(() => {})
        .finally(() => {
          fetchingProviders.current.delete(numericId);
          setLoadingProviders((prev) => {
            const next = new Set(prev);
            next.delete(numericId);
            return next;
          });
        });
    });
  }, [skeleton]); // eslint-disable-line react-hooks/exhaustive-deps

  return { skeleton, detailsMap, loadingProviders, loading, pagination, load, reset, markDeleted };
}

export function useAppointmentsSkeletonApi({ patient, cancelAppointment }) {
  const [currentVisible, setCurrentVisible] = useState(true);
  const [pastVisible, setPastVisible] = useState(false);
  const { urlData, removeParam, addParam } = useUrlParser();
  const history = useHistory();

  const patientId = patient.data?.id;

  const current = useSectionSkeleton({ patientId });
  const past = useSectionSkeleton({ patientId });

  // Track which patient we've loaded for
  const loadedPatientId = useRef(null);

  const goToPastAppointments = useCallback(() => {
    history.push(addParam('past', true).urlData.to);
    setCurrentVisible(false);
    setPastVisible(true);
  }, [addParam, history]);

  const goToCurrentAppointments = useCallback(() => {
    history.push(removeParam(['past', 'page']).urlData.to);
    setPastVisible(false);
    setCurrentVisible(true);
  }, [history, removeParam]);

  // Redirect to past if URL has ?past param
  useEffect(() => {
    if (currentVisible && urlData.params.past) {
      goToPastAppointments();
    }
  }, [urlData.params.past, currentVisible, goToPastAppointments]);

  // Load current appointments when section becomes visible or patient changes
  useEffect(() => {
    if (patientId == null || !currentVisible) return;

    const patientChanged = loadedPatientId.current !== null && loadedPatientId.current !== patientId;
    loadedPatientId.current = patientId;

    if (patientChanged) {
      // load() resets skeleton/details internally — no need for separate reset().
      // Avoid reading stale closure value of current.skeleton after reset().
      past.reset();
      current.load(false, null);
    } else if (current.skeleton === null && !current.loading) {
      current.load(false, null);
    }
  }, [patientId, currentVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load past appointments when section becomes visible, patient changes, or page changes
  const page = (() => {
    const p = +urlData.params.page;
    return p > 0 && Number.isInteger(p) ? p : 1;
  })();

  useEffect(() => {
    if (patientId == null || !pastVisible) return;

    const patientChanged = loadedPatientId.current !== null && loadedPatientId.current !== patientId;
    loadedPatientId.current = patientId;

    if (patientChanged) {
      current.reset();
      past.load(true, page);
    } else if (past.skeleton === null && !past.loading) {
      past.load(true, page);
    }
  }, [patientId, pastVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCurrentPage = useCallback(
    (newPage) => {
      past.load(true, newPage);
    },
    [past.load], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleCancelAppointment = useCallback(
    (id) =>
      cancelAppointment(id).then((action) => {
        if (action?.type === 'CANCEL_APPOINTMENT_SUCCESS') {
          current.markDeleted(id);
        }
        return action;
      }),
    [cancelAppointment, current.markDeleted], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    current,
    past,
    currentVisible,
    pastVisible,
    goToPastAppointments,
    goToCurrentAppointments,
    setCurrentPage,
    cancelAppointment: handleCancelAppointment,
  };
}
